import React, { useState, useMemo } from 'react';
import { Settings2, Check, X, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import type { ComparableFieldDefinition, ComparisonSettings } from '../../types/productComparer';

const STORAGE_KEY = 'product-comparer-settings';

/** Default fields selected for comparison. */
const DEFAULT_FIELD_IDS = [
    'ItemNumber',
    'InternalName',
    'Gtin',
    'ItemBrand',
    'ItemProductCategory',
    'NetWeightGrams',
    'Nutrient.EnergyCalories',
    'Nutrient.Fat',
    'Nutrient.Carbohydrate',
    'Nutrient.Protein',
    'Nutrient.Salt',
    'Package.Gtin',
    'Package.UnitDescriptor',
    'Package.GrossWeightGrams',
    'Package.NetWeightGrams',
];

interface ProductComparerSettingsProps {
    fields: ComparableFieldDefinition[];
    onClose: () => void;
    onSettingsChanged: (settings: ComparisonSettings) => void;
}

export function ProductComparerSettings({ fields, onClose, onSettingsChanged }: ProductComparerSettingsProps) {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as ComparisonSettings;
                return new Set(parsed.selectedFieldIds);
            }
        } catch { /* ignore */ }
        return new Set(DEFAULT_FIELD_IDS);
    });
    const [innovationStrategy, setInnovationStrategy] = useState<string>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as ComparisonSettings;
                return parsed.innovationStrategy ?? '';
            }
        } catch { /* ignore */ }
        return '';
    });
    const [salesStrategy, setSalesStrategy] = useState<string>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as ComparisonSettings;
                return parsed.salesStrategy ?? '';
            }
        } catch { /* ignore */ }
        return '';
    });

    const grouped = useMemo(() => {
        const map = new Map<string, ComparableFieldDefinition[]>();
        for (const f of fields) {
            const key = `${f.entityType} – ${f.category}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(f);
        }
        return map;
    }, [fields]);

    const toggleField = (fieldId: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(fieldId)) next.delete(fieldId);
            else next.add(fieldId);
            return next;
        });
    };

    const handleSave = () => {
        const settings: ComparisonSettings = {
            selectedFieldIds: Array.from(selectedIds),
            innovationStrategy: innovationStrategy.trim() || undefined,
            salesStrategy: salesStrategy.trim() || undefined,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        onSettingsChanged(settings);
        onClose();
    };

    const handleReset = () => {
        setSelectedIds(new Set(DEFAULT_FIELD_IDS));
        setInnovationStrategy('');
        setSalesStrategy('');
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Settings2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                        <h3 className="text-lg font-semibold">Comparison Settings</h3>
                        <p className="text-sm text-muted-foreground">
                            Select which fields from the Item graph to include in comparisons.
                        </p>
                    </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            <div className="flex items-center justify-between">
                <Badge variant="secondary">{selectedIds.size} fields selected</Badge>
                <Button variant="outline" size="sm" onClick={handleReset}>
                    <RotateCcw className="mr-1 h-3 w-3" /> Reset to defaults
                </Button>
            </div>

            <div className="max-h-[60vh] space-y-6 overflow-y-auto pr-2">
                <div className="space-y-3 rounded-md border p-3">
                    <h4 className="text-sm font-medium">Innovation Strategy (optional)</h4>
                    <textarea
                        value={innovationStrategy}
                        onChange={event => setInnovationStrategy(event.target.value)}
                        placeholder="Describe your innovation strategy, priorities, and constraints..."
                        className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                </div>

                <div className="space-y-3 rounded-md border p-3">
                    <h4 className="text-sm font-medium">Sales Strategy (optional)</h4>
                    <textarea
                        value={salesStrategy}
                        onChange={event => setSalesStrategy(event.target.value)}
                        placeholder="Describe your sales/commercial strategy, channels, and objectives..."
                        className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                </div>

                {Array.from(grouped.entries()).map(([groupName, groupFields]) => (
                    <div key={groupName}>
                        <h4 className="mb-2 text-sm font-medium text-muted-foreground">{groupName}</h4>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {groupFields.map(f => (
                                <label
                                    key={f.fieldId}
                                    className="flex cursor-pointer items-center gap-2 rounded-md border p-2 transition-colors hover:bg-accent"
                                >
                                    <Checkbox
                                        checked={selectedIds.has(f.fieldId)}
                                        onCheckedChange={() => toggleField(f.fieldId)}
                                    />
                                    <span className="text-sm">{f.displayName}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex justify-end gap-2 border-t pt-4">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave}>
                    <Check className="mr-1 h-4 w-4" /> Save Settings
                </Button>
            </div>
        </div>
    );
}

/** Load settings from localStorage (or return defaults). */
export function loadComparisonSettings(): ComparisonSettings {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored) as ComparisonSettings;
    } catch { /* ignore */ }
    return { selectedFieldIds: DEFAULT_FIELD_IDS, innovationStrategy: undefined, salesStrategy: undefined };
}
